import { useState } from "react";
import { Droplets, PieChart as PieIcon, Trophy, AlertTriangle } from "lucide-react";
import { AreaChart, Area, XAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Panel, Stat, Bar, GOLD } from "./Panel";

const FORMAT_COLORS = ["#E5C158", "#60A5FA", "#34D399", "#F472B6", "#A78BFA"];
const tip = { background: "#12141F", border: "1px solid rgba(201,162,39,.3)", borderRadius: 8, color: "#fff", fontSize: 12 };

export const VolumePanel = ({ volume, trend }) => {
  const [metric, setMetric] = useState("liters");
  return (
    <Panel title="Litri e bottiglie" icon={Droplets} testid="volume-metrics-card" className="lg:col-span-8" delay={120}>
      <div className="grid grid-cols-3 gap-4 mb-5">
        {[["today", "Oggi"], ["week", "Settimana"], ["month", "Mese"]].map(([k, label]) => (
          <div key={k} className="space-y-1">
            <Stat label={label} value={volume[k].liters} unit="L" tone="text-[#E5C158]" testid={`volume-${k}-liters`} />
            <p className="text-xs font-mono text-zinc-400" data-testid={`volume-${k}-bottles`}>{volume[k].bottles} {volume[k].bottles === 1 ? "bottiglia" : "bottiglie"}</p>
          </div>
        ))}
      </div>
      <div className="flex gap-2 mb-2">
        {[["liters", "Litri"], ["bottles", "Bottiglie"]].map(([k, label]) => (
          <button key={k} onClick={() => setMetric(k)} data-testid={`volume-toggle-${k}`}
            className={`px-3 py-1 rounded-full text-xs font-mono border transition-colors ${metric === k ? "bg-[#C9A227] border-[#C9A227] text-black" : "border-white/10 text-zinc-400 hover:text-white"}`}>{label}</button>
        ))}
        <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-zinc-500 self-center">Ultimi 14 giorni</span>
      </div>
      <div className="h-44" data-testid="volume-trend-chart">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={trend} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
            <defs>
              <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={GOLD} stopOpacity={0.45} />
                <stop offset="100%" stopColor={GOLD} stopOpacity={0} />
              </linearGradient>
            </defs>
            <XAxis dataKey="date" tick={{ fill: "#71717a", fontSize: 10 }} axisLine={false} tickLine={false} />
            <Tooltip contentStyle={tip} formatter={(v) => [v, metric === "liters" ? "Litri" : "Bottiglie"]} />
            <Area type="monotone" dataKey={metric} stroke={GOLD} strokeWidth={2} fill="url(#goldFill)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </Panel>
  );
};

export const FormatsPanel = ({ formats }) => {
  const total = formats.reduce((a, f) => a + f.bottles, 0);
  return (
    <Panel title="Formati del mese" icon={PieIcon} testid="formats-mix-card" className="lg:col-span-4" delay={180}>
      <div className="h-36">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={formats} dataKey="bottles" nameKey="format" innerRadius={42} outerRadius={64} paddingAngle={2} stroke="none">
              {formats.map((f, i) => <Cell key={f.format} fill={FORMAT_COLORS[i]} />)}
            </Pie>
            <Tooltip contentStyle={tip} />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="space-y-1.5 mt-2">
        {formats.map((f, i) => (
          <div key={f.format} className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2 text-zinc-300"><span className="w-2 h-2 rounded-full" style={{ background: FORMAT_COLORS[i] }} />{f.format}</span>
            <span className="font-mono text-white" data-testid={`format-${f.format}`}>{f.bottles} <span className="text-zinc-500 text-xs">{total ? Math.round((f.bottles / total) * 100) : 0}%</span></span>
          </div>
        ))}
      </div>
    </Panel>
  );
};

export const TopPanel = ({ top }) => {
  const max = top[0]?.bottles || 1;
  return (
    <Panel title="Top 10 profumi del mese" icon={Trophy} testid="top-perfumes-table" className="lg:col-span-8" delay={240}>
      {top.length === 0 ? <p className="text-zinc-500 text-sm">Nessuna vendita questo mese</p> : (
        <ol className="space-y-2.5">
          {top.map((p, i) => (
            <li key={p.code} className="grid grid-cols-[2rem_1fr_auto] items-center gap-3" data-testid={`top-perfume-${i}`}>
              <span className={`font-mono font-bold text-sm ${i < 3 ? "text-[#E5C158]" : "text-zinc-500"}`}>{String(i + 1).padStart(2, "0")}</span>
              <div className="min-w-0">
                <p className="text-sm text-white truncate"><span className="font-mono font-bold text-[#E5C158] mr-2">{p.code}</span>{p.name}</p>
                <Bar value={p.bottles} max={max} color={i < 3 ? GOLD : "#52525b"} />
              </div>
              <span className="font-mono text-sm text-white text-right">{p.bottles}<span className="text-zinc-500 text-xs ml-1">pz · {p.liters}L</span></span>
            </li>
          ))}
        </ol>
      )}
    </Panel>
  );
};

export const StockPanel = ({ items }) => (
  <Panel title="Scorte in esaurimento" icon={AlertTriangle} testid="inventory-alerts-card" to="/inventory" action="Inventario" className="lg:col-span-12" delay={300}>
    {items.length === 0 ? <p className="text-emerald-400 text-sm">Tutte le scorte sono sopra la soglia</p> : (
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {items.map((p) => {
          const critical = p.quantity_ml < p.alert_threshold_ml / 2;
          return (
            <li key={p.code} className={`flex items-center justify-between rounded-lg border px-3 py-2 ${critical ? "border-rose-500/40 bg-rose-500/10" : "border-amber-500/30 bg-amber-500/5"}`} data-testid={`low-stock-${p.code}`}>
              <span className="min-w-0 truncate text-sm text-zinc-200"><span className="font-mono font-bold text-white mr-2">{p.code}</span>{p.name}</span>
              <span className={`font-mono text-sm whitespace-nowrap ${critical ? "text-rose-300" : "text-amber-300"}`}>{Math.round(p.quantity_ml)} / {p.alert_threshold_ml} ml</span>
            </li>
          );
        })}
      </ul>
    )}
  </Panel>
);
